import * as THREE from 'three';
import { SketchRelationKind } from '../../../../sketch/solver/constraint/relation-compatibility';
import { SingleTargetRelation } from '../../../../sketch/solver/constraint/relationDefinition/SingleTargetRelation';
import type { SketchParabolaObject } from '../../../../mesh/sketch/SketchParabolaObject';
import { resolvePreferredEdgeLabel } from '../../model/EdgeLabelUtils';
import type { ReferenceDrivenParabolaOnCurveConstraintOptions } from '../../../../../types/reference/sketch-entity-reference/ReferenceDrivenParabolaOnCurveConstraint';

export type { ReferenceDrivenParabolaOnCurveConstraintOptions } from '../../../../../types/reference/sketch-entity-reference/ReferenceDrivenParabolaOnCurveConstraint';

/**
 * 参考驱动抛物线在曲线上关系定义。
 *
 * 焦点和顶点作为基础抛物线参数，起止点由统一参数方程保持在该曲线上。
 */
export class ReferenceDrivenParabolaOnCurveConstraint extends SingleTargetRelation<SketchParabolaObject> {
	/** 引用记录稳定标识。 */
	public readonly referenceId: string;
	/** 关系面板使用的关系类型。 */
	public relationType = SketchRelationKind.OnEdge;

	/** 投影抛物线的世界坐标焦点。 */
	private readonly projectedFocalWorld: THREE.Vector3;
	/** 投影抛物线的世界坐标顶点。 */
	private readonly projectedApexWorld: THREE.Vector3;
	/** 用于克隆和持久化一致性的投影采样点。 */
	private readonly projectedCurvePointWorlds: THREE.Vector3[];
	/** 关系面板显示的来源边标签。 */
	private readonly edgeLabel: string;

	/** @param options 外部抛物线投影参数。 */
	public constructor(options: ReferenceDrivenParabolaOnCurveConstraintOptions) {
		super();
		this.referenceId = options.referenceId;
		this.projectedFocalWorld = options.projectedFocalWorld.clone();
		this.projectedApexWorld = options.projectedApexWorld.clone();
		this.projectedCurvePointWorlds = options.projectedCurvePointWorlds.map((point) => point.clone());
		this.edgeLabel = resolvePreferredEdgeLabel({
			runtimeSourceName: options.runtimeSourceName,
			persistedSourceDisplayLabel: options.sourceDisplayLabel,
		});
		this.name = this.edgeLabel;
	}

	/** @returns 关系面板使用的稳定显示名。 */
	public override get baseDisplayName(): string {
		return this.edgeLabel;
	}

	/** @returns 与当前投影载荷相互独立的新关系实例。 */
	public override cloneSelf(): ReferenceDrivenParabolaOnCurveConstraint {
		return new ReferenceDrivenParabolaOnCurveConstraint({
			referenceId: this.referenceId,
			projectedFocalWorld: this.projectedFocalWorld.clone(),
			projectedApexWorld: this.projectedApexWorld.clone(),
			projectedCurvePointWorlds: this.projectedCurvePointWorlds.map((point) => point.clone()),
		});
	}

	/**
	 * 创建统一参数方程使用的局部基础抛物线载荷。
	 *
	 * @returns 目标草图参考面局部坐标中的焦点与顶点；目标尚未绑定时返回 `null`。
	 */
	public override createParametricFormulaPayload() {
		const target = this.getTargetOrThrow();
		if (!target) {
			return null;
		}

		const focal = target.referencePlane.worldToLocal(this.projectedFocalWorld);
		const apex = target.referencePlane.worldToLocal(this.projectedApexWorld);
		return {
			kind: 'external-parabola',
			referenceId: this.referenceId,
			focal: [focal.x, focal.y],
			apex: [apex.x, apex.y],
		};
	}
}
